import React, { useContext, useEffect, useState } from "react";
import { MainContext } from "../App";
import Api from "./api/api";
import Moment from "react-moment";
import "moment-timezone";
import "moment/locale/it";
import functions from "../utilities/functions";
import TrashIcon from "../icons/TrashIcon";
import EditIcon from "../icons/EditIcon";
import CloseIcon from "../icons/CloseIcon";
import FlowAdvanceIcon from "../icons/FlowAdvanceIcon";
import PrintIcon from "../icons/PrintIcon";

const HISTORY_TAB_STORAGE_KEY = "waiter-history-active-tab";

function HistoryPopup() {
	const {
		settings,
		table,
		setTable,
		setPopupVisible,
		setLoading,
		setAlertData,
		tempTable,
		setTempTable,
		editOrder,
		openOrder,
		ordersDisabled,
		waiterSettings,
	} = useContext(MainContext);
	const [orders, setOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [activeTab, setActiveTab] = useState(() => {
		const saved = localStorage.getItem(HISTORY_TAB_STORAGE_KEY);
		return saved === "flows" ? "flows" : "orders";
	});
	const activeTable = table ? table : tempTable;
	const currentFlow = Math.max(parseInt(activeTable?.flow || 0), 0);
	const maxFlowFromOrders = Math.max(
		0,
		...orders.flatMap((order) => (order.products || []).map((p) => parseInt(p.flow || 0)))
	);
	const maxAllowedFlow = Math.max(maxFlowFromOrders, 1);
	const isFlowEnabled = (settings.orders?.enable_flow ?? settings.waiter_app?.enable_flow) == 1;
	const isMultiCustomerTable = parseInt(activeTable?.single_order_mode) !== 1;
	const shouldGroupByCustomer =
		settings?.waiter_app?.enable_group_by_customer == 1 && isMultiCustomerTable;
	const showTableTotal = settings.waiter_app.hide_prices != 1;
	const tableLabel = /^tavolo\b/i.test(String(activeTable?.name || ""))
		? String(activeTable?.name || "")
		: `Tavolo ${activeTable?.name || ""}`;
	const headerTitle = isFlowEnabled && activeTab === "flows" ? "Portate" : "Ordini aperti";

	useEffect(() => {
		loadHistory();
	}, [table, activeTable]);

	const loadHistory = async () => {
		console.log("loadHistory");
		setLoading(true);
		const response = await Api.postProtected("/waiters_app/history/", {
			table_id: activeTable.id,
		});
		setLoading(false);
		setOrders(response.rows);
		const allProducts = response.rows.flatMap((order) => order.products || []);
		const missingFlowCount = allProducts.filter((product) => product.flow === null || product.flow === undefined)
			.length;
		console.log("history flow check", {
			total_products: allProducts.length,
			missing_flow: missingFlowCount,
		});
		if (missingFlowCount > 0) {
			console.warn("Some history products are missing flow (portata).");
		}
		let t = 0;
		for (const order of response.rows) {
			t += parseFloat(order.total);
		}
		setTotal(t);
	};

	const refreshActiveTable = async () => {
		const tablesResponse = await Api.postProtected("/waiters_app/tables/");
		const updatedTable = tablesResponse?.rows?.find((t) => t.id == activeTable.id);
		if (updatedTable) {
			if (table && table.id == updatedTable.id) setTable(updatedTable);
			if (tempTable && tempTable.id == updatedTable.id) setTempTable(updatedTable);
		}
	};

	const advanceFlow = async () => {
		console.log("advanceFlow");
		if (!activeTable.cart_id) return;
		if (currentFlow >= maxAllowedFlow) return;
		setAlertData({
			title: `Avanza portata ${currentFlow + 1}`,
			message: "Vuoi procedere?",
			showCancelButton: true,
			confirmMessage: "Si",
			cancelMessage: "No",
			callback: async function () {
				try {
					setLoading(true);
					await Api.postProtected("/tables/advance_flow/" + activeTable.id + "/", {});
					await refreshActiveTable();
					await loadHistory();
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const changeTab = (tab) => {
		setActiveTab(tab);
		localStorage.setItem(HISTORY_TAB_STORAGE_KEY, tab);
	};

	const parseNicknames = (rawNickname) => {
		if (!rawNickname) return [];
		try {
			const parsed = JSON.parse(rawNickname);
			if (!Array.isArray(parsed)) return [];
			return parsed
				.map((item) => ({
					nickname: item?.nickname ? String(item.nickname).trim() : "",
					qty: parseInt(item?.qty ?? 0),
				}))
				.filter((item) => item.nickname && item.qty > 0);
		} catch {
			return [];
		}
	};

	const normalizeNicknameLabel = (rawNickname) => {
		const parsed = parseNicknames(rawNickname);
		if (parsed.length === 0) return "";
		return parsed.map((item) => `${item.nickname} x${item.qty}`).join(", ");
	};

	const renderVariants = (variants) => {
		if (!variants || variants.length === 0) return null;
		const groups = new Map();
		for (const v of variants) {
			const key = v.variant_category_id ?? "__none__";
			if (!groups.has(key)) groups.set(key, { name: v.variant_category_name || null, items: [] });
			groups.get(key).items.push(v);
		}
		return [...groups.values()].map((group, gi) => {
			const sorted = [...group.items].sort((a, b) => (a.qty === 0 ? -1 : 1) - (b.qty === 0 ? -1 : 1));
			if (sorted.length === 0) return null;
			return (
				<div key={gi} className="small">
					{group.name && <div className="bold">{group.name}</div>}
					{sorted.map((v, vi) =>
						v.qty === 0
							? <div key={vi} className="text-error">No {v.name}</div>
							: <div key={vi}>{v.qty}X {v.name}</div>
					)}
				</div>
			);
		});
	};

	const renderProducts = (products) => {
		if (!products || products.length === 0) return null;
		const groups = new Map();
		for (const p of products) {
			if (p.qty <= 0) continue;
			const key = p.variant_category_id ?? "__none__";
			if (!groups.has(key)) groups.set(key, { name: p.variant_category_name || null, items: [] });
			groups.get(key).items.push(p);
		}
		return [...groups.values()].map((group, gi) => {
			if (group.items.length === 0) return null;
			return (
				<div key={gi} className="small">
					{group.name && <div className="bold">{group.name}</div>}
					{group.items.map((p, pi) => <div key={pi}>{p.qty}X {p.name}</div>)}
				</div>
			);
		});
	};

	const groupByUserNickname = (items) => {
		const map = new Map();
		for (const item of items) {
			const label = normalizeNicknameLabel(item?.user_nickname) || "Cliente";
			if (!map.has(label)) map.set(label, []);
			map.get(label).push(item);
		}
		return Array.from(map.entries());
	};

	const getProductsByFlow = () => {
		const flowsMap = new Map();
		for (const order of orders) {
			for (const product of order.products) {
				const flowId = parseInt(product.flow || 0);
				if (!flowsMap.has(flowId)) flowsMap.set(flowId, []);
				flowsMap.get(flowId).push({
					...product,
					order_number: order.number,
					order_id: order.id,
				});
			}
		}
		return Array.from(flowsMap.entries()).sort((a, b) => {
			if (a[0] === 0) return 1;
			if (b[0] === 0) return -1;
			return a[0] - b[0];
		});
	};

	return (
		<div className="popup md history-popup">
			<div className="container history-popup-container">
				<header>
					<div className="buttons-group">
						<div>
							<div>{headerTitle}</div>
							<div className="extrasmall">{tableLabel}</div>
						</div>
					</div>
					<div className="buttons-group history-header-actions">
						<button
							className="btn btn-tertiary btn-square"
							onClick={() => setPopupVisible(false)}
						>
							<CloseIcon />
						</button>
					</div>
				</header>
				<div className={"content history-content" + (showTableTotal ? " with-total-bar" : "")}>
					{isFlowEnabled && (
						<div className="history-tabs">
							<button
								className={activeTab == "orders" ? "active" : ""}
								onClick={() => changeTab("orders")}
							>
								Ordini
							</button>
							<button
								className={activeTab == "flows" ? "active" : ""}
								onClick={() => changeTab("flows")}
							>
								Portate
							</button>
							{activeTable.cart_id != null && (
								<div className="history-flow-controls">
									<div className="history-flow-label">
										Portata {currentFlow}/{maxAllowedFlow}
									</div>
									<button
										onClick={advanceFlow}
										className="btn btn-primary btn-square history-flow-plus-btn"
										disabled={currentFlow >= maxAllowedFlow}
									>
										<FlowAdvanceIcon />
									</button>
								</div>
							)}
						</div>
					)}
					<div className="history-panel">
						{!orders || orders.length == 0 ? (
							<div className="full-centered history-empty-state">
								Non ci sono ordini su questo tavolo
							</div>
						) : !isFlowEnabled || activeTab == "orders" ? (
							orders.map((order, i) => (
								<div key={i} className="group vertical">
									<div className="group full title">
										<div>
											<div>
												N. {order.number}{" "}
												<span className="small">ID: {order.id}</span>
											</div>
											<div className="small">
												<Moment format={"DD/MM/YYYY LT"} locale="it">
													{order.delivery_date}
												</Moment>
											</div>
										</div>
										<div className="group">
											{waiterSettings.thermal_printer_id && (
												<button
													className="btn btn-secondary btn-square btn-small btn-icon"
													onClick={() =>
														editOrder(order.id, {
															thermal_printer_id: waiterSettings.thermal_printer_id,
															prebill_request: 1,
															prebill_printed: 0,
														})
													}
												>
													<PrintIcon />
												</button>
											)}
											{!ordersDisabled && (
												<>
													<button
														className="btn btn-secondary btn-square btn-small btn-icon"
														onClick={() => openOrder(order)}
													>
														<EditIcon />
													</button>
													<button
														className="btn btn-secondary btn-square btn-small"
														onClick={() =>
															setAlertData({
																title: "Sei sicuro?",
																message: "Cancellare comanda " + order.id,
																showCancelButton: true,
																callback: async function () {
																	editOrder(order.id, {
																		status: "archived",
																	});
																},
															})
														}
													>
														<TrashIcon />
													</button>
												</>
											)}
										</div>
									</div>
									{shouldGroupByCustomer
										? groupByUserNickname(order.products).map(([label, items]) => (
											<div key={label} className="orders-group">
												<div className="orders-group-title">{label}</div>
												<div className="group vertical">
													{items.map((product, n) => (
														<div key={n} className="group full">
															<div>
																{product.qty}x {product.name}
																{renderVariants(product.variants)}
																{renderProducts(product.products)}
															</div>
															<div>
																{settings.waiter_app.hide_prices == 1
																	? ""
																	: functions.formatter.format(
																		product.qty * product.price
																	)}
															</div>
														</div>
													))}
												</div>
											</div>
										))
										: order.products.map((product, n) => (
											<div key={n} className="group full">
												<div>
													{product.qty}x {product.name}
													{renderVariants(product.variants)}
													{renderProducts(product.products)}
													<div className="small">{normalizeNicknameLabel(product.user_nickname)}</div>
												</div>
												<div>
													{settings.waiter_app.hide_prices == 1
														? ""
														: functions.formatter.format(
															product.qty * product.price
														)}
												</div>
											</div>
										))}
								</div>
							))
						) : (
							getProductsByFlow().map(([flowId, flowProducts]) => (
								<div
									key={flowId}
									className={
										"group vertical history-flow-group" +
										(flowId > currentFlow ? " history-flow-group-future" : "")
									}
								>
									<div
										className={
											"list-title " +
											(flowId === currentFlow
												? "selected history-flow-title-active"
												: "history-flow-title-inactive")
										}
									>
										{flowId > 0 ? "Portata " + flowId : "Senza portata"}
									</div>
									{flowProducts.map((product, n) => (
										<div
											key={n}
											className={
												"group full orders-group" +
												(flowId > 0 && flowId < currentFlow ? " history-product-past" : "")
											}
										>
											<div>
												{product.qty}x {product.name}
												{renderVariants(product.variants)}
												{renderProducts(product.products)}
												<div className="small">{normalizeNicknameLabel(product.user_nickname)}</div>
												<div className="small">
													Ordine {product.order_number} - ID {product.order_id}
												</div>
											</div>
											<div className="history-product-price">
												{settings.waiter_app.hide_prices == 1
													? ""
													: functions.formatter.format(
														product.qty * product.price
													)}
											</div>
										</div>
									))}
								</div>
							))
						)}
					</div>
				</div>
				{showTableTotal && (
					<footer className="history-total-bar">
						<div className="history-total-bar-label">Totale tavolo</div>
						<div className="history-total-bar-value">{functions.formatter.format(total)}</div>
					</footer>
				)}
			</div>
		</div>
	);
}

export default HistoryPopup;
